import type { ReactNode } from "react";
import { ArticleMetaLine } from "./article-meta";
import type { ArticleMetaModel } from "./types";

export interface ArticleHeaderProps {
  title: string;
  eyebrow?: ReactNode;
  dek?: ReactNode;
  meta?: ArticleMetaModel;
  minutes?: number;
  /** Header-baseline actions slot (Copy / Regenerate / Share / Edit / Apply). */
  actions?: ReactNode;
}

/**
 * The one article header: a quiet eyebrow, the `type-heading` title with an
 * actions cluster on its baseline, an optional dek, and the metadata line. Used
 * by every article kind so the top of the page reads identically everywhere.
 */
export function ArticleHeader({
  title,
  eyebrow,
  dek,
  meta,
  minutes,
  actions,
}: ArticleHeaderProps) {
  return (
    <header className="mb-9 border-b border-border/60 pb-6">
      {eyebrow ? (
        <p className="mb-2 flex items-center gap-1.5 type-subtext tracking-wide text-muted-foreground">
          {eyebrow}
        </p>
      ) : null}
      <div className="flex items-start justify-between gap-6">
        <h1 className="min-w-0 type-heading text-foreground">{title}</h1>
        {actions ? (
          <div className="flex shrink-0 items-center gap-2">{actions}</div>
        ) : null}
      </div>
      {dek ? (
        <p className="mt-3 type-body leading-body text-muted-foreground">
          {dek}
        </p>
      ) : null}
      {meta ? <ArticleMetaLine meta={meta} minutes={minutes} /> : null}
    </header>
  );
}
